/**
 * The day-to-day monitoring part of the console API: eval run history and curation of
 * guessed labels, the model-call log, and messages, cost and errors over time. `ConsoleApi` extends it and
 * `ConsoleClient` mixes `monitorMethods` in, like the kaizen part.
 */
import type { Http } from './http'
import { qs } from './http'
import type { EvalCaseView } from './kaizen'
import type { EvalResultView } from './console'

export interface EvalRunSummaryView { id: string; suite: string; passed: number; failed: number; advisory: number; created_at: string; change_set_id: string | null }
export interface EvalRunDetailView extends EvalRunSummaryView { results: EvalResultView[] }
/** Confirming a guessed label: `resolved_label` for conversation cases, `fires` for classification cases. */
export interface EvalCurateBody { confirmed: true; resolved_label?: boolean; fires?: boolean }

export interface ModelCallRowView {
  id: string; purpose: string; provider: string; model: string; ok: boolean; error: string | null
  input_tokens: number; output_tokens: number; cached_tokens: number; cost_micros: number; latency_ms: number | null
  conversation: { id: string; ref: string } | null; message_id: string | null; task_id: string | null; created_at: string
  has_prompt: boolean; has_response: boolean
}
export interface ModelCallDetailView extends ModelCallRowView {
  person_id: string | null; organisation_id: string | null
  prompt: { system: string; messages: { role: string; content: string }[]; tools: string[]; schema: string | null } | null
  prompt_removed: 'retention' | 'erasure' | 'none' | null
  response_text: string | null; response_purged_at: string | null
}
export interface ModelCallQuery { purpose?: string; model?: string; ok?: boolean; from?: string; to?: string; q?: string; conversation_id?: string; cursor?: string | null; limit?: number }

export type VolumeRange = '24h' | '7d' | '30d'
export interface VolumeView {
  range: VolumeRange; bucket: 'hour' | 'day'; from: string; to: string; timezone: string
  channels: string[]; purposes: string[]
  buckets: { at: string; label: string; received: Record<string, number>; sent: Record<string, number>; failed: number; cost_micros: Record<string, number> }[]
  totals: { received: number; sent: number; failed: number; cost_micros: number }
  compare: { received: [number, number]; sent: [number, number]; failed: [number, number]; cost_micros: [number, number]; model_failures: [number, number] }
  spend: { today_micros: number; yesterday_micros: number; yesterday_so_far_micros: number }
  errors: {
    open_alarms: { kind: string; severity: string; n: number; oldest_at: string }[]
    open_jobs: number; open_sends: number
    recent: { kind: 'send' | 'model' | 'intake'; at: string; text: string; conversation_id: string | null; ref: string | null }[]
  }
}

export interface MonitorApi {
  /** Recent eval runs (daily runs and change-set tests), newest first. */
  evalRuns(q?: { suite?: string; limit?: number; daily?: boolean }): Promise<EvalRunSummaryView[]>
  /** One run with each case's result. */
  evalRun(id: string): Promise<EvalRunDetailView>
  /** Cases whose label was guessed and waits for a person (the curation queue). */
  evalCasesToReview(suite?: string): Promise<EvalCaseView[]>
  /** Confirms a guessed label: its check now counts (supervisors and admins). */
  curateEvalCase(id: string, body: EvalCurateBody): Promise<EvalCaseView>
  /** The model-call log (Studio, AI, Calls). */
  modelCalls(q?: ModelCallQuery): Promise<{ items: ModelCallRowView[]; next_cursor: string | null }>
  modelCall(id: string): Promise<ModelCallDetailView>
  /** Messages, AI cost and errors over time (Analytics, Volume and cost). */
  volume(q?: { range?: VolumeRange; bucket?: 'hour' | 'day' }): Promise<VolumeView>
}

const enc = encodeURIComponent

export function monitorMethods(http: Http): MonitorApi {
  return {
    evalRuns: (q = {}) => http.get<EvalRunSummaryView[]>('/evals/runs', { suite: q.suite, limit: q.limit, daily: q.daily ? 1 : undefined }),
    evalRun: (id) => http.get<EvalRunDetailView>(`/evals/runs/${enc(id)}`),
    evalCasesToReview: (suite) => http.get<EvalCaseView[]>(`/evals/cases${qs({ suite, needs_review: 1 })}`),
    curateEvalCase: (id, body) => http.patch<EvalCaseView>(`/evals/cases/${enc(id)}`, body),
    modelCalls: (q = {}) => http.get<{ items: ModelCallRowView[]; next_cursor: string | null }>('/model-calls', { ...q, ok: q.ok === undefined ? undefined : String(q.ok), cursor: q.cursor ?? undefined }),
    modelCall: (id) => http.get<ModelCallDetailView>(`/model-calls/${enc(id)}`),
    volume: (q = {}) => http.get<VolumeView>('/analytics/volume', q),
  }
}
